import { FC, useCallback, useState } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDebounce, useEffectOnceWhen } from "rooks";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../../../store/hooks";
import { largestCostChangesSetupChangeThunk } from "../../../../../../../../../store/dashboards/thunks/widgets/largest-cost-changes/largestCostChangesSetupChangeThunk";
import { largestCostChangesSetupPropertyByKeySelector } from "../../../../../../../../../store/dashboards/selectors/setups/larges-cost-changes/largestCostChangesSetupPropertyByKeySelector";

interface LargestCostChangesMinChangeProps {
  type: "minCostChange" | "minPercentageChange";
}
export const LargestCostChangesMinChange: FC<
  LargestCostChangesMinChangeProps
> = ({ type }) => {
  const [localMinDataChange, setLocalMinDataChange] = useState<{
    state?: boolean;
    value: number | null;
  } | null>(null);

  const dispatch = useAppDispatch();
  const minDataChange = useAppSelector(
    largestCostChangesSetupPropertyByKeySelector(type),
  );

  const costChangeHandler = useCallback(
    (value: string) => {
      dispatch(
        largestCostChangesSetupChangeThunk({
          [type]: {
            state: true,
            value: +value,
          },
        }),
      );
    },
    [dispatch, type],
  );
  const setDebouncedValue = useDebounce(costChangeHandler, 1000);

  const textFieldChangeHandler = useCallback(
    (event: any) => {
      const value = event.target.value;
      setLocalMinDataChange({
        ...localMinDataChange,
        value: value,
      });
      setDebouncedValue(value);
    },
    [setDebouncedValue, localMinDataChange],
  );
  const checkboxChangeHandler = useCallback(() => {
    setLocalMinDataChange({
      value: localMinDataChange?.value ?? null,
      state: !localMinDataChange?.state,
    });
    dispatch(
      largestCostChangesSetupChangeThunk({
        [type]: {
          value: localMinDataChange?.value ? +localMinDataChange.value : null,
          state: !localMinDataChange?.state,
        },
      }),
    );
  }, [localMinDataChange, dispatch, type]);

  useEffectOnceWhen(() => {
    setLocalMinDataChange(minDataChange);
  }, !!minDataChange);

  return (
    <FormControl size="small" fullWidth>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              checked={localMinDataChange?.state ?? false}
              onChange={checkboxChangeHandler}
            />
          }
          label={
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body1">{dataByType[type].label}</Typography>
              <Tooltip title={dataByType[type].tooltip} placement="right" arrow>
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </Stack>
          }
        />
        <TextField
          sx={{
            width: 120,
          }}
          size="small"
          label={dataByType[type].textFieldLabel}
          value={localMinDataChange?.value ?? ""}
          onChange={textFieldChangeHandler}
          type="number"
          disabled={!localMinDataChange?.state}
          autoFocus={true}
        />
      </Stack>
    </FormControl>
  );
};

const dataByType = {
  minCostChange: {
    tooltip: "Cost changes less than “Min cost change” will not be shown.",
    textFieldLabel: "Amount ($)",
    label: "Min cost change",
  },
  minPercentageChange: {
    tooltip:
      "Cost changes less than “Min percentage change” will not be shown.",
    textFieldLabel: "Percent (%)",
    label: "Min percentage change",
  },
};
